import React from 'react';
import { Link } from 'react-router-dom';
import { ArrowRight, CheckCircle, Users, Award, Target, TrendingUp } from 'lucide-react';
import AIAssistantPopup from '../components/AIAssistantPopup';

const Home = () => {
  const values = [
    {
      icon: <Award className="w-12 h-12 text-blue-800" />,
      title: 'Proven Track Record',
      description: 'We have a history of success in helping our clients achieve their goals with measurable results.'
    },
    {
      icon: <TrendingUp className="w-12 h-12 text-blue-800" />,
      title: 'Forward-Thinking Approach',
      description: 'We are constantly innovating to stay ahead of the curve and provide cutting-edge solutions.'
    },
    {
      icon: <Users className="w-12 h-12 text-blue-800" />,
      title: 'Trusted Partnership',
      description: 'We believe in building strong, long-term relationships with our clients based on trust and excellence.'
    }
  ];

  const stats = [
    { number: '500+', label: 'Clients Served' },
    { number: '15+', label: 'Years Experience' },
    { number: '98%', label: 'Client Satisfaction' },
    { number: '24/7', label: 'Support Available' }
  ];

  const featuredServices = [
    {
      title: 'Bookkeeping & Accounting',
      description: 'Professional financial management to keep your business organized and compliant.',
      icon: '📊'
    },
    {
      title: 'Business Automation',
      description: 'Streamline your workflows and boost productivity with intelligent automation solutions.',
      icon: '⚙️'
    },
    {
      title: 'Payroll Services',
      description: 'Comprehensive payroll management to ensure your team is paid accurately and on time.',
      icon: '💰'
    }
  ];

  return (
    <div className="min-h-screen" data-testid="home-page">
      {/* Hero Section */}
      <section className="relative bg-gradient-to-br from-blue-900 via-blue-700 to-indigo-900 text-white py-24 md:py-40 overflow-hidden" data-testid="hero-section">
        {/* Animated background elements */}
        <div className="absolute inset-0 opacity-10">
          <div className="absolute top-20 left-10 w-72 h-72 bg-white rounded-full mix-blend-overlay filter blur-3xl animate-pulse"></div>
          <div className="absolute bottom-20 right-10 w-96 h-96 bg-blue-300 rounded-full mix-blend-overlay filter blur-3xl animate-pulse delay-1000"></div>
        </div>
        
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative z-10">
          <div className="text-center">
            <h1 className="text-4xl md:text-6xl lg:text-7xl font-bold mb-6 leading-tight animate-fadeIn" data-testid="hero-title">
              We Empower Your Business
            </h1>
            <p className="text-xl md:text-2xl mb-10 text-blue-100 max-w-3xl mx-auto leading-relaxed" data-testid="hero-description">
              Professional services tailored to your Book-Keeping & Accounting and all other business & personal needs. We're dedicated to helping our clients achieve their goals through excellence and innovation.
            </p>
            
            {/* Clean, Professional Logo - Like a Real Picture */}
            <div className="flex flex-col items-center mb-12">
              <img 
                src="https://customer-assets.emergentagent.com/job_digital-home-48/artifacts/nj84rh1g_image.png" 
                alt="Professional Services Nationwide" 
                className="w-auto h-48 md:h-64 lg:h-80 object-contain animate-fadeIn"
                style={{ border: 'none', maxWidth: '90%' }}
              />
              <p className="text-2xl md:text-3xl text-blue-100 italic font-light tracking-wide mt-6">
                Where Innovations Meet Opportunity
              </p>
            </div>
            
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <Link
                to="/services"
                className="inline-flex items-center justify-center px-8 py-4 bg-white text-blue-800 font-semibold rounded-lg hover:bg-blue-50 transform hover:scale-105 transition-all duration-200 shadow-2xl hover:shadow-3xl"
                data-testid="hero-cta-services"
              >
                Explore Services
                <ArrowRight className="ml-2" size={20} />
              </Link>
              <Link
                to="/contact"
                className="inline-flex items-center justify-center px-8 py-4 bg-transparent border-2 border-white text-white font-semibold rounded-lg hover:bg-white hover:text-blue-800 transform hover:scale-105 transition-all duration-200 shadow-lg"
                data-testid="hero-cta-contact"
              >
                Contact Us
              </Link>
            </div>
          </div>
        </div>
      </section>

      {/* Stats Section */}
      <section className="py-16 bg-gradient-to-r from-blue-50 via-indigo-50 to-blue-50" data-testid="stats-section">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-2 md:grid-cols-4 gap-8">
            {stats.map((stat, index) => (
              <div key={index} className="text-center transform hover:scale-110 transition-transform duration-300" data-testid={`stat-${index}`}>
                <div className="text-4xl md:text-5xl font-bold bg-gradient-to-r from-blue-700 to-indigo-700 bg-clip-text text-transparent mb-2">
                  {stat.number}
                </div>
                <div className="text-gray-700 font-semibold">{stat.label}</div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Values Section */}
      <section className="py-20" data-testid="values-section">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-4" data-testid="values-title">
              Why Choose Professional Services Nationwide?
            </h2>
            <p className="text-xl text-gray-600 max-w-2xl mx-auto">
              We bring expertise, dedication, and results to every client relationship.
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            {values.map((value, index) => (
              <div
                key={index}
                className="bg-gradient-to-br from-white to-blue-50 p-8 rounded-xl shadow-lg hover:shadow-2xl transform hover:-translate-y-2 transition-all duration-300 border border-blue-100"
                data-testid={`value-card-${index}`}
              >
                <div className="mb-4 transform hover:scale-110 transition-transform duration-300">{value.icon}</div>
                <h3 className="text-xl font-bold text-gray-900 mb-3">{value.title}</h3>
                <p className="text-gray-600 leading-relaxed">{value.description}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Featured Services */}
      <section className="py-20 bg-gray-50" data-testid="featured-services-section">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-4" data-testid="featured-services-title">
              Featured Services
            </h2>
            <p className="text-xl text-gray-600 max-w-2xl mx-auto">
              Comprehensive solutions to meet your business needs
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            {featuredServices.map((service, index) => (
              <div
                key={index}
                className="bg-white p-8 rounded-xl shadow-md hover:shadow-2xl transform hover:-translate-y-2 transition-all duration-300 border-t-4 border-blue-600 hover:border-blue-800"
                data-testid={`featured-service-${index}`}
              >
                <div className="text-6xl mb-4 animate-float">{service.icon}</div>
                <h3 className="text-xl font-bold text-gray-900 mb-3">{service.title}</h3>
                <p className="text-gray-600 mb-4 leading-relaxed">{service.description}</p>
                <Link
                  to="/services"
                  className="text-blue-800 font-semibold hover:text-blue-900 inline-flex items-center group"
                  data-testid={`learn-more-${index}`}
                >
                  Learn more
                  <ArrowRight className="ml-1 group-hover:translate-x-1 transition-transform" size={16} />
                </Link>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-20 bg-gradient-to-r from-blue-800 via-blue-700 to-indigo-800 text-white relative overflow-hidden" data-testid="cta-section">
        <div className="absolute inset-0 opacity-10">
          <div className="absolute top-10 right-20 w-64 h-64 bg-white rounded-full mix-blend-overlay filter blur-3xl"></div>
          <div className="absolute bottom-10 left-20 w-80 h-80 bg-blue-300 rounded-full mix-blend-overlay filter blur-3xl"></div>
        </div>
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center relative z-10">
          <h2 className="text-3xl md:text-4xl font-bold mb-6" data-testid="cta-title">
            Ready to Elevate Your Business?
          </h2>
          <p className="text-xl mb-8 text-blue-100" data-testid="cta-description">
            Let's discuss how our professional services can help you achieve your goals.
          </p>
          <Link
            to="/contact"
            className="inline-flex items-center justify-center px-8 py-4 bg-white text-blue-800 font-semibold rounded-lg hover:bg-blue-50 transform hover:scale-105 transition-all duration-200 shadow-2xl hover:shadow-3xl"
            data-testid="cta-button"
          >
            Get Started Today
            <ArrowRight className="ml-2" size={20} />
          </Link>
        </div>
      </section>

      {/* AI Assistant Popup */}
      <AIAssistantPopup />
    </div>
  );
};

export default Home;